/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Wrap,
    WrapItem,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CloseIcon} from '../../../components/icons'
import {REMOVE_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const SelectedRefinements = ({toggleFilter, selectedFilterValues, filters, handleReset}) => {
    const {formatMessage} = useIntl()
    const priceFilterValues = filters?.find((filter) => filter.attributeId === 'price')

    const selectedFilters = useMemo(() => {
        const result = []
        for (const key in selectedFilterValues) {
            const values = selectedFilterValues[key]?.split('|') || []
            const matchingFilter = filters?.find((f) => f.attributeId === key)
            const displayLabel = matchingFilter?.label || key

            values.forEach((filterVal) => {
                const uiLabel =
                    key === 'price'
                        ? priceFilterValues?.values?.find(
                              (priceFilter) => priceFilter?.value === filterVal
                          )?.label
                        : filterVal

                if (key !== 'htype' && key !== 'cgid') {
                    result.push({
                        uiLabel: uiLabel,
                        filterType: displayLabel,
                        value: key,
                        apiLabel: filterVal
                    })
                }
            })
        }
        return result
    }, [selectedFilterValues, filters, priceFilterValues])

    return (
        <Flex
            alignItems="start"
            flexDirection={'column'}
            justifyContent="space-between"
            marginTop={{base: '0', sm: '3'}}
            marginBottom={{base: '4', sm: '0'}}
        >
            <Box className="refinement-caps-block" pos={'relative'}>
                <Wrap
                    direction="row"
                    align="center"
                    display="flex"
                    flexWrap="wrap"
                    data-testid="sf-selected-refinements"
                    className="caps-block"
                >
                    {selectedFilters?.map((filter, idx) => (
                        <WrapItem key={idx}>
                            <Box marginLeft={0} marginRight={1}>
                                <Button
                                    colorScheme="gray"
                                    iconSpacing={1}
                                    rightIcon={
                                        <Box
                                            position="absolute"
                                            top="5px"
                                            right="5px"
                                            width="15px"
                                            height="15px"
                                            background="bodybgColor"
                                            borderRadius="50%"
                                        >
                                            <CloseIcon
                                                color="black"
                                                boxSize="20px"
                                                ml={'4px'}
                                                mt={'4px'}
                                                width={'auto'}
                                                height={'auto'}
                                            />
                                        </Box>
                                    }
                                    onClick={() =>
                                        toggleFilter({value: filter.apiLabel}, filter.value, true)
                                    }
                                    aria-label={formatMessage(REMOVE_FILTER, {
                                        label: filter.uiLabel
                                    })}
                                    className="refinement-btn"
                                    p="5px 30px 5px 10px"
                                    bg="#dadada"
                                    borderRadius="50px"
                                    fontSize="12px"
                                    lineHeight="15px"
                                    height="auto"
                                    whiteSpace="unset"
                                    textTransform={'inherit'}
                                    position="relative"
                                    data-refinementname={filter.filterType}
                                    data-refinementvalue={filter.apiLabel}
                                >
                                    {filter.uiLabel}
                                </Button>
                            </Box>
                        </WrapItem>
                    ))}
                </Wrap>
            </Box>
            {selectedFilters.length > 0 && (
                <Button
                    className="clearFliters"
                    onClick={handleReset}
                    fontSize={'xs'}
                    marginRight={'10px'}
                    textDecoration="underline"
                    textTransform="lowercase"
                    color="orangeBaseColor"
                    padding={2}
                    mt={2}
                    variant="link"
                    size="sm"
                    lineHeight={5}
                    fontWeight={'medium'}
                    _active={{
                        color: 'orangeBaseColor'
                    }}
                >
                    <FormattedMessage
                        defaultMessage="clear all"
                        id="selected_refinements.action.clear_all"
                    />
                </Button>
            )}
        </Flex>
    )
}

SelectedRefinements.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func
}

export default SelectedRefinements
